import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, of, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { moduleRoutes } from './urlmap';
import { moduleRoutes as userRoutes } from '@user/urlmap';
import { ApplicationHttpClient } from '@base/http-client';
import { MatSidenav } from '@angular/material';
import { HttpErrorResponse } from '@angular/common/http';
import { ExceptionService } from '@base/exceptions.service';
import { ROLES, EMPTY, getNumber } from '@utils';
import { ICompany } from '@company/company.structures';
import { IUser, IErrorResponse, ITableauLink, ITableauWorkbook, IWorkbook,
  ICreateUpdateCompany } from '@interfaces';
import * as _ from 'lodash';
import { ModuleService } from '@module/module.service';
import * as fromModels from '@models';

@Injectable()
export class CompanyService {
  private _mapRoutes = moduleRoutes;
  private _sidenav: MatSidenav;

  private _company: ICompany;
  private bsCompany = new BehaviorSubject<ICompany>(this._company);

  private companiesState: ICompany[] = [];
  private bsCompaniesState = new BehaviorSubject<ICompany[]>(this.companiesState);

  private tableauLinksState: ITableauLink[] = [];
  private bsTableauLinks = new BehaviorSubject<ITableauLink[]>(this.tableauLinksState);
  private _processingImages = false;
  private bsProcessingImages = new BehaviorSubject<boolean>(this._processingImages);
  private _busyService = false;
  private _bsBusyStatus = new BehaviorSubject<boolean>(this._busyService);

  constructor(
    private http: ApplicationHttpClient,
    private exceptionService: ExceptionService,
    private moduleService: ModuleService,
  ) {
    this._company = EMPTY;
  }

  getCompanies(user: IUser | fromModels.User = null, queryParams: object = null) {
    this._bsBusyStatus.next(true);
    let filteredList = false;
    let finalUrl = this._mapRoutes.list;

    if (user !== null) {
      const id = (user.hasOwnProperty('id')) ? user.id : user['user_id'];
      const userRoleAllowed = (user.role['code'] === ROLES.ANALYST || user.role['code'] === ROLES.CUSTOMER);

      if (typeof id !== 'undefined' && userRoleAllowed) {
        filteredList = true;
        finalUrl = `${userRoutes.getUser}${id}/companies/`;
      }
    }

    this.http.Get(finalUrl, queryParams).subscribe(
      (res) => {
        this._bsBusyStatus.next(false);
        if (!filteredList) {
        } else if (filteredList) {
        }
        this.companiesState = res['results'];
        this.bsCompaniesState.next(this.companiesState);
      },
      (err: HttpErrorResponse) => {
        this._bsBusyStatus.next(false);
        const error: IErrorResponse = this.exceptionService.errorHandling(err);
      });
  }

  getCompany(id): Observable<ICompany> {
    this._bsBusyStatus.next(true);
    const url = `${this._mapRoutes.list}${id}/`;
    return this.http.Get(url).pipe(
      map((company: ICompany) => {
        this._bsBusyStatus.next(false);
        return company;
      }),
      catchError((err: HttpErrorResponse) => {
        this._bsBusyStatus.next(false);
        return throwError(err);
      })
    );
  }

  createCompany(company: ICompany): Observable<ICreateUpdateCompany> {
    this._bsBusyStatus.next(true);
    const auxObject = { ...company };

    return this.http.Post(this._mapRoutes.create, company).pipe(
      map((res: ICreateUpdateCompany) => {
        const companyModules = [...res.company.modules] as [];
        const createdCompany = {...res.company};
        this._bsBusyStatus.next(false);
        this.companiesState.unshift(res.company);
        this.moduleService.updateCompanyInModules(companyModules, createdCompany);
        this.bsCompaniesState.next(this.companiesState);
        return res;
      }),
      catchError((err: HttpErrorResponse) => {
        console.log(err.error);
        this._bsBusyStatus.next(false);
        return throwError(err);
      })
    );
  }

  public setSidenav(sidenav: MatSidenav) {
    this._sidenav = sidenav;
  }

  public openSidenav() {
    return this._sidenav.open();
  }

  public closeSidenav() {
    return this._sidenav.close();
  }

  public toggleSidenav(): void {
    if (typeof this._sidenav !== 'undefined') {
      this._sidenav.toggle();
    }
  }

  private updateModules(company: ICompany) {
    const modules = [...company.modules];
    let newModules = [];

    modules.map((module) => {
      if (company['related']) {
        newModules = [...newModules, company];
      }
    });

    return newModules;
  }

  updateState(company: ICompany, reordering = true): void {
    const auxListCompanies = [...this.companiesState];
    const objIndex = auxListCompanies.findIndex(
      ((obj: ICompany) => obj.id === company.id)
    );

    // actualizar la lista de modules asociados
    // company.modules = this.updateModules(company);

    // actualizar info y mover el usuario al inicio de la lista...
    auxListCompanies[objIndex] = company;
    if (reordering) {
      const editedCompany = auxListCompanies.splice(objIndex, 1);
      auxListCompanies.unshift(editedCompany[0]);
    }

    // actualizar el state de las empresas...
    this.companiesState = auxListCompanies;
    this.bsCompaniesState.next(this.companiesState);
  }

  updateCompany(company: ICompany): Observable<ICreateUpdateCompany> {
    this._bsBusyStatus.next(true);
    const auxCompany = { ...company };
    const auxWorkbooks = auxCompany.hasOwnProperty('workbooks') ? auxCompany.workbooks : EMPTY;
    this.updateState(auxCompany);

    return this.http.Put(`${this._mapRoutes.list}${company.id}/`, company).pipe(
      map((res: ICreateUpdateCompany) => {
        this._bsBusyStatus.next(false);
        return res;
      }),
      catchError(err => {
        this._bsBusyStatus.next(false);
        return throwError(err);
      })
    );
  }

  deleteCompany(company: Partial<ICompany>): Observable<any> {
    this._bsBusyStatus.next(true);

    return this.http.Delete(`${this._mapRoutes.list}${company.id}/`).pipe(
      map(response => {
        // actualizar el store de los usuarios...
        const objIndex = this.companiesState.findIndex(((obj: ICompany) => obj.id === company.id));
        const deleted = this.companiesState.splice(objIndex, 1);
        this.bsCompaniesState.next(this.companiesState);
        this._bsBusyStatus.next(false);
        return true;
      }),
      catchError((err: HttpErrorResponse) => {
        this._bsBusyStatus.next(false);
        return throwError(err);
      })
    );
  }

  observeCompanies(): Observable<ICompany[]> {
    return this.bsCompaniesState.asObservable();
  }

  companySelectedChanged(): Observable<ICompany> {
    return this.bsCompany.asObservable();
  }

  setCompany(company: ICompany): void {
    this._company = company;
    this.bsCompany.next(this._company);
  }

  setCompanies(companies: ICompany[]): void {
    this.companiesState = companies;
    this.bsCompaniesState.next(this.companiesState);
  }

  getCurrentCompanies(): ICompany[] {
    return this.companiesState;
  }

  getCompaniesDashChart(queryParams: object = null) {
    return this.http.Get(this._mapRoutes.dashChart, queryParams);
  }

  tableauWorkbooksSync(workbooks: ITableauWorkbook[]) {
    const companies = [...this.companiesState];
    console.log('WORKBOOKS LLEGANDO en tableauWorkbooksSync', workbooks);

    if (companies.length && workbooks.length) {
      // const groupedWorbooks = _.groupBy(workbooks, 'project_id');
      const groupedMap = Array.from(workbooks.reduce(
        (entryMap, e) => entryMap.set(e.project['id'], [...entryMap.get(e.project['id']) || [], e]),
        new Map()
        ));
      const groupedWorbooks = {};
      groupedMap.map(e => {
        groupedWorbooks[e[0]] = e[1];
      });

      let companiesWithTableauModule: ICompany[] = [];
      companiesWithTableauModule = companies.filter((c: ICompany) => c.tableau_id !== EMPTY);

      companiesWithTableauModule.map((c: ICompany) => {
        const companyWorkbooks = (groupedWorbooks.hasOwnProperty(c.tableau_id)) ? [...groupedWorbooks[c.tableau_id]] : [];
        c.workbooks = companyWorkbooks as [];

        c.workbooks.map((coWb: ITableauWorkbook) => {
          coWb.tableau_id = coWb.id;
          coWb.company_id = c.id;

          coWb['thumbnail'] = EMPTY;
        });

        // actualizar el state de empresas...  <--
        const pos = companies.findIndex((cs: ICompany) => cs.id === c.id);
        companies[pos] = c;
      });

      // notificar los cambios a todos los observers...
      if (companiesWithTableauModule) {
        // this.companiesState = companies;
        this.bsCompaniesState.next(companies);
      }
    } else {
      //
    }
  }

  synchronizeTableau() {
    this._bsBusyStatus.next(true);

    return this.http.Get(this._mapRoutes.sync).pipe(
      map((res: ICompany[]) => {
        this._bsBusyStatus.next(false);
        res.map(c => {
          this.updateState(c, false);
        });
        return true;
      }),
      catchError((err: HttpErrorResponse) => {
        this._bsBusyStatus.next(false);
        console.log('EMPRESAS ERROR');
        console.error(`${err.error}`);
        return throwError(false);
      })
    );
  }

  getTableauLinks(company: ICompany): Observable<ITableauLink[]> {
    if (company !== EMPTY) {
      this._bsBusyStatus.next(true);
      const endPoint = `${this._mapRoutes.list}${company.id}/tableau-links/`;

      return this.http.Get(endPoint).pipe(
        map((res: ITableauLink[]) => {
          this._bsBusyStatus.next(false);
          this.tableauLinksState = res;
          this.bsTableauLinks.next(this.tableauLinksState);
          return res;
        }),
        catchError((err: HttpErrorResponse) => {
          this._bsBusyStatus.next(false);
          console.log(err.message);
          return [];
        })
      );
    }
  }

  observeTableauLinks(): Observable<ITableauLink[]> {
    return this.bsTableauLinks.asObservable();
  }

  updateTableauLink(link: ITableauLink, status: boolean, groupTableauId: string) {
    let tableauLinksCopy = [...this.tableauLinksState];
    const pos = tableauLinksCopy.findIndex((tl: ITableauLink) =>
      (tl.group_id === link.group_id && tl.workbook_id === link.workbook_id));
    const payload = {
      workbook_id: link.workbook_id,
      group_id: link.group_id,
      status: status,
      group_tableau_id: groupTableauId,
    };

    if (status) {
      if (pos < 0) {
        link.id = EMPTY;
        tableauLinksCopy = [...tableauLinksCopy, link];
      }
    } else if (!status) {
      const deleted = tableauLinksCopy.splice(pos, 1);
    }

    this.tableauLinksState = tableauLinksCopy;
    this.bsTableauLinks.next(this.tableauLinksState);
    return this.http.Post(this._mapRoutes.updateTableauLink, payload);
  }

  refreshTags(companyIds: number[]) {
    this._bsBusyStatus.next(true);
    const payload = {ids: companyIds, };

    return this.http.Post(`${this._mapRoutes.list}companies-tags/`, payload).pipe(
      map((res: any) => {
        this._bsBusyStatus.next(false);
        return res['results'];
        }
      ),
      catchError((err: HttpErrorResponse) => {
        this._bsBusyStatus.next(false);
        return [];
      })
    );
  }

  getWorkbooks(company: ICompany) {
    this._bsBusyStatus.next(true);
    return this.http.Get(`${this._mapRoutes.list}${company.id}/workbooks/`).pipe(
      map((workbooks: IWorkbook[]) => {
        this._bsBusyStatus.next(false);
        const objIndex = this.companiesState.findIndex((c: ICompany) => c.id === company.id);
        if (objIndex >= 0) {
          const companyWorkbooks: [] = workbooks as [];
          this.companiesState[objIndex].workbooks = companyWorkbooks;
        }
        return workbooks;
      }),
      catchError((err: HttpErrorResponse) => {
        this._bsBusyStatus.next(false);
        console.log(err.message);
        return company.workbooks;
      })
    );
  }

  refreshPreviewImages(company: ICompany, force = false) {
    if (typeof company !== 'undefined' && company !== EMPTY) {
      const forceRefresh: object = {force_workbooks: Boolean(force)};
      this.bsProcessingImages.next(true);
      return this.http.Get(`${this._mapRoutes.list}${company.id}/refresh-preview-image/`, forceRefresh).pipe(
        map((res) => {
          this.bsProcessingImages.next(false);
          return res;
        }),
        catchError((e: HttpErrorResponse) => {
          this.bsProcessingImages.next(false);
          console.error(e.message);
          const responseBody = {data: [], results: []};
          return of(responseBody);
        })
      );
    }
  }

  observeStatusProcessingImg(): Observable<boolean> {
    return this.bsProcessingImages.asObservable();
  }

  observeBusyStatus(): Observable<boolean> {
    return this._bsBusyStatus.asObservable();
  }

  synchronizeCompanyWorkbooks(company: ICompany) {
    const endpoint = `${this._mapRoutes.list}${company.id}/synchronize-tableau-workbooks/`;
    return this.http.Get(endpoint).pipe(
      map(res => res),
      catchError((err: HttpErrorResponse) => throwError(err))
    );
  }

  getAllCompanies(user: IUser = null, queryParams: object = null): Observable<ICompany[]> {
    this._bsBusyStatus.next(true);
    let finalUrl = this._mapRoutes.list;

    if (user !== null) {
      const id = (user.hasOwnProperty('id')) ? user.id : user['user_id'];
      const userRoleAllowed = (user.role['code'] === ROLES.ANALYST || user.role['code'] === ROLES.CUSTOMER);

      if (typeof id !== 'undefined' && userRoleAllowed) {
        finalUrl = `${userRoutes.getUser}${id}/companies/`;
      }
    }

    return this.http.Get(finalUrl, queryParams).pipe(
      map((res) => {
        const results: ICompany[] = res['results'];
        this._bsBusyStatus.next(false);
        this.companiesState = results;
        this.bsCompaniesState.next(this.companiesState);
        return results;
      }),
      catchError((err: HttpErrorResponse) => {
        this._bsBusyStatus.next(false);
        const error: IErrorResponse = this.exceptionService.errorHandling(err);
        return throwError(err);
      })
    );
  }

  companyJobs(companyId: number): Observable<fromModels.Etl[]> {
    if (companyId !== EMPTY && companyId > 0) {
      const endpoint = `${this._mapRoutes.list}${companyId}/jobs/`;
      return this.http.Get(endpoint).pipe(
        map((res: fromModels.ApiResponse) => {
          let results: fromModels.Etl[];
          results = res.results;
          return results;
        }),
        catchError((err: HttpErrorResponse) => throwError(err))
      );
    } else {
      return of([]);
    }
  }
}
